import { AppLayout } from '@/components/app-layout';
import { AuthCheck } from '@/components/auth-check.component';
import { OnboardingCheck } from '@/components/onboarding-check.component';
import { LeadListLeadsContainer } from '@/containers/lists/$listId/lead-list-leads-container.component';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import { z } from 'zod';

const listSearchSchema = z.object({
  openDrawer: z
    .enum([
      'import-csv',
      'manual-entry',
      'import-crm',
      'reverse-email-lookup',
      // ⌘K "This list" actions (command-palette.component.tsx) land
      // directly on these panels.
      'find-email',
      'find-phone',
      // Home's "Find personal emails" quick start.
      'find-personal-email',
      'export-csv',
      'people-search',
      'custom-ai-column',
      'import-company-csv',
      // CLE-321: deep-link from the Agents catalog "Use in list" picker.
      // These map to the actions-drawer AgentRunPanel for the matching
      // agent_type so the user lands directly on the configure-and-run
      // screen for the list they picked.
      'agent-run-research',
      'agent-run-qualification',
      'agent-run-custom',
    ])
    .optional()
    // .catch on every param here: an unknown value (stale link, typo)
    // falls back instead of throwing, which crashed the whole list page.
    .catch(undefined),
  // 'agents' (CLE-321) is the new canonical value; 'smart-agents' is
  // kept in the enum for backward compatibility with existing links and
  // bookmarks. LeadListLeadsContainer normalizes 'smart-agents' to 'agents'
  // before passing to the actions drawer.
  drawerTab: z
    .enum(['sources', 'enrichments', 'agents', 'smart-agents', 'exports'])
    .optional()
    .catch(undefined),
  sheet: z.enum(['contacts', 'companies']).optional().catch(undefined),
  aiFilters: z.string().optional(),
  templateId: z.string().optional(),
  // Deep-link from the catalog "Your custom agents" Use-in-list picker.
  // Auto-opens the agent-run-custom panel pre-loaded with this saved
  // agent. See AgentRunPanel for how the id is consumed.
  customAgentId: z.string().uuid().optional().catch(undefined),
});

export const Route = createFileRoute('/lists/$listId/')({
  component: RouteComponent,
  validateSearch: listSearchSchema,
});

function RouteComponent() {
  const {
    openDrawer,
    drawerTab,
    // `sheet` stays in the schema (below) but is intentionally not read
    // here: the container subscribes to it directly via useSearch so the
    // active tab tracks the URL after mount, not just on it.
    aiFilters,
    templateId,
    customAgentId,
  } = useSearch({
    from: '/lists/$listId/',
  });

  return (
    <>
      {/* The tab title is the list's name, set by LeadListLeadsContainer. */}
      <AuthCheck>
        <OnboardingCheck>
          <AppLayout variant='full-bleed'>
            <LeadListLeadsContainer
              initialDrawerView={openDrawer}
              initialDrawerTab={drawerTab}
              initialAiFilters={aiFilters}
              initialTemplateId={templateId}
              initialCustomAgentId={customAgentId}
            />
          </AppLayout>
        </OnboardingCheck>
      </AuthCheck>
    </>
  );
}
